import { useQuery, useMutation } from "react-query";
import {
  addBranch,
  deleteBranch,
  getAllBranches,
} from "../../sdk/branches/branch";
import { Building2, Globe, MapPin, Plus, Search, Trash2 } from "lucide-react";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useState } from "react";
import AddBranch from "../../components/add-branch/AddBranch";
import { useError } from "../../context/ErrorProvider";
import AddBranchSuccess from "../../components/add-branch/AddBranchSuccess";
import { SkeletonLoansPage } from "../../skeletons/LoansSkeletons";
import DeletePrompt from "../../components/delete-branch/DeletePrompt";
import DeleteBranchSuccess from "../../components/delete-branch/DeleteBranchSuccess";

const Branches = () => {
  const formattedDate = useFormattedDate();
  const [errors, setErrors] = useState({});
  const [branchDetails, setBranchDetails] = useState({
    name: "",
    city: "",
  });
  const [openAddBranch, setOpenAddBranch] = useState(false);
  const [openAddBranchSuccess, setOpenAddBranchSuccess] = useState(false);
  const [openDeleteBranchSuccess, setOpenDeleteBranchSuccess] = useState(false);
  const [openDeletePrompt, setOpenDeletePrompt] = useState(false);
  const { showError } = useError();
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDeleteBranch, setSelectedDeleteBranch] = useState(null);

  const toggleAddBranch = () => {
    setOpenAddBranch(true);
  };
  const closeAddBranch = () => {
    setOpenAddBranch(false);
  };

  const toggleDeletePrompt = () => {
    setOpenDeletePrompt(true);
  };
  const closeDeletePrompt = () => {
    setOpenDeletePrompt(false);
  };

  const toggleDeleteBranchSuccess = () => {
    setOpenDeleteBranchSuccess(true);
  };
  const closeDeleteBranchSuccess = () => {
    setOpenDeleteBranchSuccess(false);
  };

  const toggleAddBranchSuccess = () => {
    setOpenAddBranchSuccess(true);
  };
  const closeAddBranchSuccess = () => {
    setOpenAddBranchSuccess(false);
  };

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const {
    data: branches,
    refetch: refetchBranches,
    isLoading,
  } = useQuery({
    queryKey: ["all branches"],
    queryFn: () => getAllBranches(),
  });

  const { mutate: createBranchMutate, isLoading: loadingCreateBranch } =
    useMutation({
      mutationKey: ["add branch"],
      mutationFn: () => addBranch(branchDetails?.name, branchDetails?.city),
      onSuccess: () => {
        closeAddBranch();
        refetchBranches();
        toggleAddBranchSuccess();
      },
      onError: (error) => {
        showError(error?.response?.data?.message || error.message);
      },
    });

  const { mutate: deleteBranchMutate, isLoading: loadingDeleteBranch } =
    useMutation({
      mutationKey: ["delete branch"],
      mutationFn: () => deleteBranch(selectedDeleteBranch?.id),
      onSuccess: () => {
        closeDeletePrompt();
        refetchBranches();
        setSelectedDeleteBranch(null);
        toggleDeleteBranchSuccess();
      },
      onError: (error) => {
        showError(error?.response?.data?.message || error.message);
      },
    });

  const validateBranch = () => {
    let newErrors = {};

    if (!branchDetails.name || branchDetails.name.trim() === "") {
      newErrors.name = "Branch name is required.";
    }

    if (!branchDetails.city || branchDetails.city.trim() === "") {
      newErrors.city = "City is required.";
    }

    return newErrors;
  };

  const handleAddBranch = () => {
    const validationErrors = validateBranch();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    createBranchMutate();
  };

  const handleDeleteBranch = () => {
    deleteBranchMutate();
  };

  return (
    <>
      {openDeletePrompt && (
        <DeletePrompt
          closeDeletePrompt={closeDeletePrompt}
          handleContinueDeletion={handleDeleteBranch}
          loading={loadingDeleteBranch}
        />
      )}

      {openDeleteBranchSuccess && (
        <DeleteBranchSuccess
          closeDeleteBranchSuccess={closeDeleteBranchSuccess}
        />
      )}

      {openAddBranchSuccess && (
        <AddBranchSuccess closeAddBranchSuccess={closeAddBranchSuccess} />
      )}
      {openAddBranch && (
        <AddBranch
          closeAddBranch={closeAddBranch}
          branchDetails={branchDetails}
          setBranchDetails={setBranchDetails}
          errors={errors}
          clearError={clearError}
          handleAddBranch={handleAddBranch}
          loading={loadingCreateBranch}
        />
      )}
      {isLoading ? (
        <SkeletonLoansPage />
      ) : (
        <div className="w-full pl-[10px]">
          <div className="flex items-center h-[60px] mb-[10px] justify-between">
            <div className="flex items-center gap-[5px]">
              <span className="font-bold text-[20px] text-[#000000]">
                Branches
              </span>
            </div>
            <button
              onClick={toggleAddBranch}
              className="bg-[#000000] h-[38px] rounded-[30px] text-[#ffffff] text-[14px] font-normal px-[20px] flex items-center justify-center gap-[5px]"
            >
              <Plus size={16} color="#ffffff" />
              Create a Branch
            </button>
          </div>
          <div>
            {branches.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center py-36 px-6 bg-white border-2 border-gray-100 relative overflow-hidden">
                {/* Background Decorative Element */}
                <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
                  <Globe
                    size={400}
                    className="absolute -top-20 -right-20 text-[#2A8C52]"
                  />
                </div>

                {/* Icon Container with ZipGo Green glow */}
                <div className="relative mb-8">
                  <div className="absolute inset-0 bg-[#2A8C52] blur-[35px] opacity-15 rounded-full scale-150" />

                  <div className="relative w-24 h-24 bg-white rounded-3xl flex items-center justify-center border border-gray-100 shadow-2xl shadow-gray-200/40">
                    <Building2
                      size={42}
                      className="text-[#2A8C52] opacity-90"
                    />

                    {/* Small Location Badge */}
                    <div className="absolute -bottom-1 -right-1 w-9 h-9 bg-[#2A8C52] rounded-xl flex items-center justify-center border-4 border-white shadow-lg">
                      <MapPin size={16} className="text-white" fill="white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="max-w-md text-center relative z-10">
                  <h3 className="text-gray-900 font-extrabold text-2xl tracking-tight">
                    No Branches Found
                  </h3>
                  <p className="text-gray-500 mt-3 text-[15px] leading-relaxed">
                    Your branch network is currently empty. Start by setting up
                    your first physical location or headquarters to begin
                    managing operations.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto relative z-10">
                  <button className="sm:w-auto px-8 py-3.5 bg-[#2A8C52] text-white text-sm font-bold rounded-2xl hover:bg-[#227042] hover:shadow-xl hover:shadow-green-900/20 transition-all flex items-center justify-center gap-2 group">
                    <Plus
                      size={18}
                      className="group-hover:scale-125 transition-transform"
                    />
                    Register New Branch
                  </button>

                  <button className="sm:w-auto px-8 py-3.5 bg-white text-gray-600 text-sm font-bold rounded-2xl border border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 shadow-sm">
                    <Search size={18} />
                    Check Other Regions
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-6">
            {branches?.length > 0 &&
              branches.map((branch) => (
                <div
                  key={branch.id}
                  onClick={() => setSelectedBranch(branch)}
                  className="px-4 py-2 shadow-md cursor-pointer rounded-xl border bg-white relative"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDeleteBranch(branch);
                      toggleDeletePrompt();
                    }}
                    className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </button>

                  <div className="flex justify-between items-center pr-6">
                    <h2 className="font-bold text-lg">{branch?.name}</h2>

                    {branch?.isHeadOffice && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        Head Office
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm">City: {branch?.city}</p>
                  <p className="text-gray-600 text-sm">Code: {branch?.code}</p>
                  <p className="text-gray-400 text-xs mt-2">
                    Created: {formattedDate(branch?.createdAt)}
                  </p>
                </div>
              ))}
          </div>
        </div>
      )}

      {selectedBranch && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[600px] rounded-xl p-6 shadow-xl relative">
            <h2 className="text-xl font-bold mb-3">{selectedBranch.name}</h2>
            {selectedBranch.isHeadOffice && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                Head Office
              </span>
            )}

            <div className="mt-4 space-y-2">
              <p>
                <span className="font-semibold">City:</span>{" "}
                {selectedBranch.city}
              </p>
              <p>
                <span className="font-semibold">Code:</span>{" "}
                {selectedBranch.code}
              </p>
              <p>
                <span className="font-semibold">Created:</span>{" "}
                {formattedDate(selectedBranch.createdAt)}
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedBranch(null)}
                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Branches;
